import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container } from '@fiverr-private/layout_components';
import { getContext } from '@fiverr-private/fiverr_context';
import { withVideoQuality } from '../../shared/cloudinary';
import { MIXPANEL_EVENTS, sendMixpanelEvent } from '../../shared/tracking';
import useNetworkVideoQuality from '../../hooks/useNetworkVideoQuality';
import {
  HERO_CTA_TEXT,
  HERO_SUBTITLE,
  HERO_TITLE,
  HOLLYWOOD_HERO_VIDEO_POSTER,
  HOLLYWOOD_HERO_VIDEO_POSTER_MOBILE,
  HOLLYWOOD_HERO_VIDEO_URL,
  HOLLYWOOD_HERO_VIDEO_URL_MOBILE,
} from './constants';
import { content, ctaButton, section, subtitle, textWrap, title, video } from './styles.ve.css';

interface HeroSectionProps {
  onExploreClick?: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onExploreClick }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { isMobile } = getContext();
  const quality = useNetworkVideoQuality();
  const qualityRef = useRef(quality);
  qualityRef.current = quality;
  const baseVideoUrl = isMobile ? HOLLYWOOD_HERO_VIDEO_URL_MOBILE : HOLLYWOOD_HERO_VIDEO_URL;
  const posterUrl = isMobile ? HOLLYWOOD_HERO_VIDEO_POSTER_MOBILE : HOLLYWOOD_HERO_VIDEO_POSTER;
  const [isInView, setIsInView] = useState(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const videoUrl = useMemo(() => withVideoQuality(baseVideoUrl, qualityRef.current), [baseVideoUrl, isInView]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);

        if (entry.isIntersecting) {
          el.play().catch(() => undefined);
        } else {
          el.pause();
          el.removeAttribute('src');
          el.load();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  const handleExploreClick = () => {
    sendMixpanelEvent({ eventName: MIXPANEL_EVENTS.CLICKED_HERO_CTA });
    onExploreClick?.();
  };

  return (
    <Container className={section}>
      <video
        ref={videoRef}
        className={video}
        src={isInView ? videoUrl : undefined}
        poster={posterUrl}
        autoPlay
        muted
        loop
        playsInline
      />
      <Container className={content}>
        <Container className={textWrap}>
          <h1 className={title}>{HERO_TITLE}</h1>
          <p className={subtitle}>{HERO_SUBTITLE}</p>
          <button type="button" className={ctaButton} onClick={handleExploreClick}>
            {HERO_CTA_TEXT}
          </button>
        </Container>
      </Container>
    </Container>
  );
};

export default HeroSection;
